/**
 * Message seed data generator
 */

import { faker } from '@faker-js/faker'
import { Message, Project, MessageStatus, MessageReadStatus } from '@/types'

const messageTitles = [
  'Project Update',
  'Meeting Notes',
  'Design Feedback',
  'Question about timeline',
  'Budget Discussion',
  'Feature Request',
  'Bug Report',
  'Weekly Status',
  'Client Feedback',
  'Team Announcement',
]

/**
 * Generate a single message
 */
export function generateMessage(project: Project, isDirectMessage: boolean = false): Message {
  const creator = faker.helpers.arrayElement(project.users)
  const otherUsers = project.users.filter((u) => u.id !== creator.id)

  // Need at least 1 other user for messages
  if (otherUsers.length === 0) {
    throw new Error('Project must have at least 2 users to generate messages')
  }

  // For direct messages, select one other user
  // For group messages, select 1-4 users (or all available if fewer)
  const recipients = isDirectMessage
    ? [faker.helpers.arrayElement(otherUsers)]
    : faker.helpers.arrayElements(
        otherUsers,
        faker.number.int({ min: 1, max: Math.min(4, otherUsers.length) })
      )

  const allUsers = [creator, ...recipients]
  const createdAt = faker.date.past({ years: 0.5 })

  // Generate read status - creator always reads their own message
  // Recipients have 60% chance of having read the message
  const readBy: MessageReadStatus[] = [
    {
      userId: creator.id,
      readAt: createdAt, // Creator reads at creation time
    },
  ]

  // Add read status for recipients
  recipients.forEach((recipient) => {
    if (faker.datatype.boolean({ probability: 0.6 })) {
      readBy.push({
        userId: recipient.id,
        readAt: faker.date.between({ from: createdAt, to: new Date() }),
      })
    }
  })

  return {
    id: faker.string.uuid(),
    createdAt,
    updatedAt: faker.date.recent({ days: 7 }),
    creator,
    status: MessageStatus.Published,
    name: faker.helpers.arrayElement(messageTitles),
    description: faker.lorem.paragraphs(faker.number.int({ min: 1, max: 3 })),
    directMessage: isDirectMessage,
    workspace: project.workspace,
    tasks: [],
    users: allUsers,
    documents: [],
    projects: [project],
    messages: [], // Threaded replies
    readBy,
  }
}

/**
 * Generate multiple messages for a project
 */
export function generateMessages(project: Project, count: number = 15): Message[] {
  const messages: Message[] = []

  // 40% direct messages, 60% group messages
  const directCount = Math.floor(count * 0.4)
  const groupCount = count - directCount

  // Generate direct messages
  for (let i = 0; i < directCount; i++) {
    messages.push(generateMessage(project, true))
  }

  // Generate group messages
  for (let i = 0; i < groupCount; i++) {
    messages.push(generateMessage(project, false))
  }

  // Add some threaded replies (about 30% of messages)
  const messagesWithReplies = faker.helpers.arrayElements(
    messages,
    Math.floor(messages.length * 0.3)
  )

  messagesWithReplies.forEach((parentMessage) => {
    const replyCount = faker.number.int({ min: 1, max: 3 })
    const replies: Message[] = []

    for (let i = 0; i < replyCount; i++) {
      const reply = generateMessage(project, parentMessage.directMessage)
      reply.name = `Re: ${parentMessage.name}`

      // 50% chance of having nested replies (replies to replies)
      if (faker.datatype.boolean()) {
        const nestedReplyCount = faker.number.int({ min: 1, max: 2 })
        const nestedReplies: Message[] = []

        for (let j = 0; j < nestedReplyCount; j++) {
          const nestedReply = generateMessage(project, parentMessage.directMessage)
          nestedReply.name = `Re: ${reply.name}`
          nestedReplies.push(nestedReply)
        }

        reply.messages = nestedReplies
      }

      replies.push(reply)
    }

    parentMessage.messages = replies
  })

  return messages
}
